import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SubdomainLink } from './SubdomainLink';
import { Menu, X, User, Home, Info, Search, MessageSquare, Crown, LifeBuoy, LogOut, Camera, BookOpen, ChevronDown, ChevronRight, CreditCard, History, Star, Building2, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authService, vendorService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import authStorage from '../utils/authStorage';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [displayName, setDisplayName] = useState('demo');
  const [mobileVendorOpen, setMobileVendorOpen] = useState(false);
  const [mobileMagazineOpen, setMobileMagazineOpen] = useState(false);
  const location = useLocation();
  const { isAdmin, roles, logout } = useAuth();
  const { toast } = useToast();
  const isAdminPath = location.pathname.toLowerCase().startsWith('/admin');

  useEffect(() => {
    setMobileVendorOpen(false);
    setMobileMagazineOpen(false);
  }, [location.pathname]);

  // Vendor Profile Photo Upload States & Helper Functions
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("photo", file);

    setUploading(true);
    try {
      const res = await vendorService.uploadProfilePhoto(formData);
      if (res.success) {
        toast.success("Profile picture updated successfully!");
        if (res.data && res.data.photoUrl) {
          const storedUser = authStorage.getItem('user');
          if (storedUser) {
            const userObj = JSON.parse(storedUser);
            userObj.photoUrl = res.data.photoUrl;
            authStorage.setItem('user', JSON.stringify(userObj));
          }
          setProfilePhotoUrl(res.data.photoUrl);
          window.dispatchEvent(new Event('profilePhotoChanged'));
        }
      } else {
        toast.error("Failed to upload profile picture: " + res.error);
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred during upload.");
    } finally {
      setUploading(false);
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getAdminRoleName = () => {
    if (!roles) return 'Admin Console';
    if (roles.includes('ROLE_SUPER_ADMIN')) return 'Super Admin Console';
    if (roles.includes('ROLE_ADMIN')) return 'Admin Console';
    if (roles.includes('ROLE_SALES_ADMIN')) return 'Sales Console';
    if (roles.includes('ROLE_LISTING_ADMIN')) return 'Listing Console';
    if (roles.includes('ROLE_SUPPORT_ADMIN')) return 'Support Console';
    if (roles.includes('ROLE_MAGAZINE_SALES')) return 'Magazine Sales Console';
    return 'Admin Console';
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateUserProfileData = async () => {
      const storedUser = authStorage.getItem('user');
      if (!storedUser) return;
      
      try {
        const userObj = JSON.parse(storedUser);
        const initialName = userObj.name || (userObj.emailId || userObj.username || 'demo');
        setDisplayName(initialName);
        setProfilePhotoUrl(userObj.photoUrl || '');

        const email = userObj.emailId || userObj.username;
        if (email && email.includes('@')) {
          const res = await authService.getUserProfile(email);
          if (res.success && res.data) {
            const profile = res.data;
            if (profile.name) {
              setDisplayName(profile.name);
            }
            if (profile.photoUrl) {
              setProfilePhotoUrl(profile.photoUrl);
            }
            authStorage.setItem('user', JSON.stringify({
              ...userObj,
              id: profile.id,
              name: profile.name,
              emailId: profile.emailId,
              photoUrl: profile.photoUrl || ''
            }));
          }
        }
      } catch (err) {
        console.error("Error setting Navbar profile data:", err);
      }
    };

    updateUserProfileData();
    window.addEventListener('storage', updateUserProfileData);
    window.addEventListener('profilePhotoChanged', updateUserProfileData);
    return () => {
      window.removeEventListener('storage', updateUserProfileData);
      window.removeEventListener('profilePhotoChanged', updateUserProfileData);
    };
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Vendors', path: '/listing' },
  ];

  return (
    <nav className={`${
      isAdminPath 
        ? `fixed ${scrolled ? 'bg-[#23977a] shadow-md py-2' : 'bg-transparent py-4'} border-transparent` 
        : `absolute bg-white border-b border-gray-100 ${scrolled ? 'shadow-md py-2' : 'py-4'}`
    } w-full z-[100] transition-all duration-300`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex justify-between items-center">
          {/* Main Logo from public folder */}
          <SubdomainLink to={isAdmin() ? "/admin" : "/"} className="flex items-center">
            <img
              src="/Top_Interior_India_1.png"
              alt="Top Interiors India"
              className={`h-10 min-[380px]:h-11 sm:h-12 md:h-14 w-auto object-contain transition-all ${isAdminPath ? 'brightness-0 invert' : ''}`}
            />
          </SubdomainLink>

          {/* Center Links */}
          <div className="hidden lg:flex items-center space-x-12 relative -translate-x-[2%]">
            {!isAdmin() && navLinks.map((link) => (
              <SubdomainLink
                key={link.name}
                to={link.path}
                className={`text-[16px] font-medium transition-colors hover:text-primary ${location.pathname === link.path ? 'text-primary' : 'text-secondary'}`}
              >
                {link.name}
              </SubdomainLink>
            ))}
          </div>

          {/* Right Auth Links */}
          <div className="hidden lg:flex items-center space-x-2">
            {authStorage.getItem('token') ? (
              isAdmin() ? (
                <div className="flex items-center space-x-6">
                  <SubdomainLink to="/admin" className={`text-[14px] font-bold transition-opacity px-2 ${isAdminPath ? 'text-white hover:text-white/80' : 'text-[#2ab391] hover:opacity-85'}`}>
                    {getAdminRoleName()}
                  </SubdomainLink>
                  <button 
                    onClick={logout}
                    className={`text-[14px] font-semibold transition-all active:scale-95 ${
                      isAdminPath 
                        ? 'text-white bg-white/10 hover:bg-white/20 border border-white/20 px-3.5 py-1.5 rounded-lg backdrop-blur-sm' 
                        : 'text-red-600 hover:text-red-700'
                    }`}
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <SubdomainLink to="/my-account/manage-listing" className="flex items-center gap-2 border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50 transition-colors">
                    <User size={14} className="text-gray-500" />
                    <span className="text-[13px] font-medium text-gray-700">{displayName}</span>
                  </SubdomainLink>
                </div>
              )
            ) : (
              <>
                <SubdomainLink to="/sign-up" className="text-[14px] font-medium text-primary hover:opacity-80 transition-opacity">
                  Sign Up
                </SubdomainLink>
                <span className="text-gray-300">|</span>
                <SubdomainLink to="/login" className="text-[14px] font-medium text-primary hover:opacity-80 transition-opacity">
                  Login
                </SubdomainLink>
              </>
            )}
          </div>

          {/* Mobile Menu Button & Guest Auth Links */}
          <div className="lg:hidden flex items-center gap-1 sm:gap-2">
            {!authStorage.getItem('token') && (
              <div className="flex items-center gap-1 sm:gap-1.5 mr-1">
                <SubdomainLink
                  to="/login"
                  className="text-[10px] min-[360px]:text-[11px] sm:text-xs font-bold text-primary border border-primary/20 hover:bg-primary/5 transition-all py-1 px-2 min-[360px]:py-1.5 min-[360px]:px-2.5 rounded-lg bg-white"
                >
                  Login
                </SubdomainLink>
                <SubdomainLink
                  to="/sign-up"
                  className="text-[10px] min-[360px]:text-[11px] sm:text-xs font-bold text-white bg-primary hover:bg-opacity-90 transition-all py-1 px-2 min-[360px]:py-1.5 min-[360px]:px-2.5 rounded-lg shadow-xs"
                >
                  Sign Up
                </SubdomainLink>
              </div>
            )}
            <button onClick={() => setIsOpen(!isOpen)} className={`${isAdminPath ? 'text-white' : 'text-secondary'} p-2`}>
              <Menu size={28} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-xs lg:hidden"
            />

            {/* Sliding Drawer */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 bottom-0 z-[120] w-[280px] max-w-full bg-white shadow-2xl flex flex-col lg:hidden border-r border-gray-100 text-left"
            >
              {authStorage.getItem('token') && !isAdmin() ? (
                /* Profile Header Card for Logged-In Vendors (No Logo) */
                <div className="bg-primary text-white p-6 relative flex flex-row items-center gap-4 select-none pt-8 border-b border-[#23977a]/30">
                    <button 
                      onClick={() => setIsOpen(false)} 
                      className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
                    >
                      <X size={20} />
                    </button>
                    
                    {/* Left Side: Avatar/Profile Pic */}
                    <div 
                      className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center relative border-2 border-white/25 overflow-hidden cursor-pointer group flex-shrink-0"
                      onClick={triggerFileSelect}
                    >
                      {profilePhotoUrl ? (
                        <img src={getImageUrl(profilePhotoUrl)} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User size={30} className="text-white/80" />
                      )}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera size={14} className="text-white" />
                      </div>
                      {uploading && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>

                    {/* Right Side: Name, ID, Change Profile Action */}
                    <div className="flex flex-col gap-0.5 text-left min-w-0 flex-1">
                      <h3 className="font-extrabold text-sm truncate text-white leading-tight">{displayName}</h3>
                      <p className="text-[10px] text-teal-50 truncate font-semibold leading-normal">
                        {(() => {
                          try {
                            const userObj = JSON.parse(authStorage.getItem('user') || '{}');
                            return userObj.emailId || userObj.username || '';
                          } catch(e) {
                            return '';
                          }
                        })()}
                      </p>
                      <button 
                        type="button" 
                        onClick={triggerFileSelect} 
                        className="text-[10px] font-bold text-white/90 hover:text-white transition-colors underline text-left mt-1 outline-none uppercase tracking-wider"
                      >
                        {profilePhotoUrl ? 'Change Profile Photo' : 'Upload Profile Photo'}
                      </button>
                    </div>
                    
                    <input 
                      type="file" 
                      ref={fileInputRef} 
                      onChange={handleFileChange} 
                      accept="image/*" 
                      className="hidden" 
                    />
                  </div>
              ) : (
                /* Standard Minimal Header for Admin/Guests (No Logo) - Clean Close Button Only */
                <div className="flex items-center justify-end px-5 py-4 border-b border-gray-100 bg-slate-50">
                  <button onClick={() => setIsOpen(false)} className="text-secondary hover:text-primary p-1">
                    <X size={20} />
                  </button>
                </div>
              )}

              {/* Menu Navigation Content */}
              <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
                {/* General Explore Section */}
                <div className="space-y-1">
                  <span className="block text-[9px] font-extrabold text-gray-400 uppercase tracking-widest mb-2 px-3">Explore</span>
                  {[
                    { name: 'Home', path: '/', icon: <Home size={16} /> },
                    { name: 'Vendors', path: '/listing', icon: <Search size={16} /> }
                  ].map(link => (
                    <SubdomainLink
                      key={link.name}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all hover:bg-slate-50 hover:text-primary ${
                        location.pathname === link.path ? 'bg-teal-50/50 text-[#2ab391]' : 'text-gray-600'
                      }`}
                    >
                      {link.icon}
                      {link.name}
                    </SubdomainLink>
                  ))}
                </div>


                {/* ── Vendor Collapsible Menu ─────────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <div className="border-t border-gray-100 pt-4 space-y-1 text-left">
                    <button
                      onClick={() => setMobileVendorOpen(!mobileVendorOpen)}
                      className="flex items-center justify-between w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-700 hover:bg-slate-50 transition-colors"
                    >
                      <span className="flex items-center gap-3">
                        <Building2 size={16} className="text-[#2ab391]" />
                        <span>Vendor</span>
                      </span>
                      {mobileVendorOpen ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                    </button>

                    {mobileVendorOpen && (
                      <div className="pl-6 space-y-0.5 mt-1 border-l border-slate-150 ml-5 text-left">
                        {[
                          { name: 'My Profile', path: '/my-account/profile', icon: <User size={14} /> },
                          { name: 'Vendor Listing', path: '/my-account/manage-listing', icon: <FileText size={14} /> },
                          { name: 'Inquiry', path: '/my-account/inquiries', icon: <MessageSquare size={14} /> },
                          { name: 'Upgrade Membership', path: '/my-account/upgrade-membership', icon: <Crown size={14} /> },
                          { name: 'Reviews', path: '/my-account/reviews', icon: <Star size={14} /> },
                        ].map(link => (
                          <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:bg-slate-50 hover:text-primary ${
                              location.pathname === link.path ? 'bg-teal-50/50 text-[#2ab391]' : 'text-gray-600'
                            }`}
                          >
                            {link.icon}
                            {link.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Magazine Collapsible Menu ────────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <div className="border-t border-gray-100 pt-4 space-y-1 text-left">
                    <button
                      onClick={() => setMobileMagazineOpen(!mobileMagazineOpen)}
                      className="flex items-center justify-between w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-700 hover:bg-slate-50 transition-colors"
                    >
                      <span className="flex items-center gap-3">
                        <BookOpen size={16} className="text-[#2ab391]" />
                        <span>Magazine</span>
                      </span>
                      {mobileMagazineOpen ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                    </button>

                    {mobileMagazineOpen && (
                      <div className="pl-6 space-y-0.5 mt-1 border-l border-slate-150 ml-5 text-left">
                        {[
                          { name: 'Your Subscription', path: '/my-account/magazine/subscription', icon: <CreditCard size={14} /> },
                          { name: 'Subscribe', path: '/my-account/magazine/subscribe', icon: <BookOpen size={14} /> },
                          { name: 'History of Payment', path: '/my-account/magazine/history', icon: <History size={14} /> },
                        ].map(link => (
                          <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:bg-slate-50 hover:text-primary ${
                              location.pathname === link.path ? 'bg-teal-50/50 text-[#2ab391]' : 'text-gray-650'
                            }`}
                          >
                            {link.icon}
                            {link.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Help & Support Top-level Menu ─────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <div className="border-t border-gray-100 pt-4 text-left">
                    <Link
                      to="/my-account/support-tickets"
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all hover:bg-slate-50 hover:text-primary ${
                        location.pathname === '/my-account/support-tickets' ? 'bg-teal-50/50 text-[#2ab391]' : 'text-gray-700'
                      }`}
                    >
                      <LifeBuoy size={16} className="text-[#2ab391]" />
                      Help & Support
                    </Link>
                  </div>
                )}

                {/* Auth Actions or Admin Control */}
                <div className="border-t border-gray-100 pt-6">
                  {authStorage.getItem('token') ? (
                    isAdmin() ? (
                      <div className="space-y-1">
                        <span className="block text-[9px] font-extrabold text-gray-400 uppercase tracking-widest mb-2 px-3">Administration</span>
                        <SubdomainLink
                          to="/admin"
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-[#2ab391] hover:bg-slate-50"
                        >
                          <User size={16} />
                          {getAdminRoleName()}
                        </SubdomainLink>
                        <button
                          onClick={() => {
                            setIsOpen(false);
                            logout();
                          }}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-red-500 hover:bg-red-50 w-full text-left transition-colors"
                        >
                          <LogOut size={16} />
                          Sign Out
                        </button>
                      </div>
                    ) : (
                      /* Vendor Log Out */
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          logout();
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-red-500 hover:bg-red-50 w-full text-left transition-colors"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    )
                  ) : null}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
